import { clone } from "lodash";
import { getDefaultBlockName } from "utils/document/blockNameUtils";

function getParentId(ps) {
  const parent = ps?.product_service_parent_id;
  if (Array.isArray(parent)) return parent[0] ?? null;
  if (parent) return String(parent);
  return null;
}

/**
 * Normalizes template/document product services into a flat list so bundle
 * children are discoverable whether they arrive nested on `children` or flat
 * with `is_inside_bundle`.
 */
export function flattenProductServicesForDropdown(productServices) {
  const flat = [];
  const seenIds = new Set();

  const addPs = (ps, parentId = null) => {
    const id = ps?.document_item_product_service_id;
    if (!id || seenIds.has(String(id))) return;
    seenIds.add(String(id));

    const isChild = Boolean(parentId) || Boolean(ps.is_inside_bundle);
    flat.push({
      ...ps,
      is_inside_bundle: isChild,
      is_bundle: isChild ? false : Boolean(ps.is_bundle),
      product_service_parent_id: parentId
        ? [parentId]
        : ps.product_service_parent_id
          ? Array.isArray(ps.product_service_parent_id)
            ? ps.product_service_parent_id
            : [ps.product_service_parent_id]
          : []
    });
  };

  (productServices || []).forEach((ps) => {
    if (ps.is_inside_bundle) return;
    addPs(ps);
    const isBundleParent = ps.is_bundle || (ps.children?.length > 0);
    if (isBundleParent && ps.children?.length) {
      [...ps.children]
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
        .forEach((child) => addPs(child, ps.document_item_product_service_id));
    }
  });

  (productServices || []).forEach((ps) => {
    if (ps.is_inside_bundle) {
      addPs(ps);
    }
  });

  return flat;
}

/**
 * Builds dropdown options for Change/Remove Item actions.
 * Bundle parents are followed by their children (indented in the select UI).
 */
export function buildProductServiceItemDropdownOptions({
  productServices,
  productServiceItemIds,
  layout,
  excludedTemplateIds = new Set()
}) {
  const isExcluded = (id) => id != null && excludedTemplateIds.has(String(id));

  const flatList = flattenProductServicesForDropdown(productServices).filter(
    (ps) => !isExcluded(ps.document_item_product_service_id)
  );
  const sorted = clone(flatList).sort((a, b) => {
    if (a.document_item_id < b.document_item_id) return 1;
    if (a.document_item_id > b.document_item_id) return -1;
    return 0;
  });

  const topLevel = sorted.filter((ps) => !ps.is_inside_bundle);
  const childrenByParentId = sorted.reduce((acc, ps) => {
    if (!ps.is_inside_bundle) return acc;
    const parentId = getParentId(ps);
    if (!parentId) return acc;
    if (!acc[parentId]) acc[parentId] = [];
    acc[parentId].push(ps);
    return acc;
  }, {});

  let counter = 1;
  const addedBlockHeaders = new Set();

  return topLevel.reduce((acc, current) => {
    if (isExcluded(current.document_item_product_service_id)) {
      return acc;
    }

    const blockKey = current.document_item_id;
    if (productServiceItemIds?.length > 1 && !addedBlockHeaders.has(blockKey)) {
      const layoutItem = layout?.find((l) => l.i === current.document_item_id);
      const blockName = layoutItem ? getDefaultBlockName(layout, layoutItem) : `Product Service ${counter}`;
      acc.push({ code: "subHeader", subHeader: blockName });
      addedBlockHeaders.add(blockKey);
      counter += 1;
    }

    acc.push({
      code: current.document_item_product_service_id,
      name: `Qty: ${current.quantity} - desc : ${current.short_description}`,
      document_item_id: current.document_item_id,
      is_bundle_child: false
    });

    const isBundleParent = current.is_bundle || (current.children?.length > 0);
    if (isBundleParent) {
      const parentId = String(current.document_item_product_service_id);
      const children = childrenByParentId[parentId] || [];
      children.forEach((child) => {
        if (isExcluded(child.document_item_product_service_id)) return;
        acc.push({
          code: child.document_item_product_service_id,
          name: `Qty: ${child.quantity} - desc : ${child.short_description}`,
          document_item_id: child.document_item_id,
          is_bundle_child: true
        });
      });
    }

    return acc;
  }, []);
}

export function isBundleChildSelection(productServices, selectedId) {
  if (!selectedId || !productServices?.length) return false;
  const flatList = flattenProductServicesForDropdown(productServices);
  const selected = flatList.find(
    (ps) => String(ps.document_item_product_service_id) === String(selectedId)
  );
  return Boolean(selected?.is_inside_bundle);
}
